import React, { useState, useEffect, useRef } from 'react';
import { Heart, Star, PlayCircle, Award, Film, Instagram, Twitter, Youtube, Play, ChevronLeft, ChevronRight, X, Calendar, TrendingUp, Globe, Users, Crown, Sparkles, Camera, Palette, Bookmark, Music, Zap, Target, Moon, Cloud, Sun } from 'lucide-react';

const Actor = () => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [liked, setLiked] = useState(false);
  const [playTrailer, setPlayTrailer] = useState(false);
  const [hoveredMovie, setHoveredMovie] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [gradientAngle, setGradientAngle] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [animationPhase, setAnimationPhase] = useState(0);
  const containerRef = useRef(null);

  const images = [
    {
      id: 1,
      src: "https://media.gettyimages.com/id/2213604565/photo/new-york-new-york-shah-rukh-khan-attends-the-2025-met-gala-celebrating-superfine-tailoring.jpg?s=612x612&w=0&k=20&c=0n0hqZ3iarYNG7AYgoiRPmyZhEb3W3B4B856363FX3Q=",
      alt: "Shah Rukh Khan - King of Bollywood",
      title: "The King",
      year: "2023"
    },
    {
      id: 2,
      src: "https://media.gettyimages.com/id/499342275/photo/2003-portrait-of-shahrukh-khan.jpg?s=612x612&w=0&k=20&c=U37PZBFoT2vd26kmL1rWcXRi9TYkw6MlNDQBz5-RaeI=",
      alt: "SRK - Romantic Icon",
      title: "Romantic Icon",
      year: "Classic"
    },
    {
      id: 3,
      src: "https://media.gettyimages.com/id/499342415/photo/2001-shahrukh-khan-posing.jpg?s=612x612&w=0&k=20&c=9ztucLhMf8qRQMZ4iEnAjceaC6sLz9G39YFnFToIdQc=",
      alt: "Shah Rukh Khan - Style Icon",
      title: "Style Icon",
      year: "Modern"
    },
    {
      id: 4,
      src: "https://media.gettyimages.com/id/183559156/photo/sydney-australia-bollywood-actor-shahrukh-khan-performs-live-for-fans-at-allphones-arena-on.jpg?s=612x612&w=0&k=20&c=0F5TNJj-1nv9T7HW_EWKCO8H-yFfYtjogHC4ngEhzks=",
      alt: "SRK - International Star",
      title: "Global Star",
      year: "Worldwide"
    },
    {
      id: 5,
      src: "https://media.gettyimages.com/id/135278777/photo/dubai-united-arab-emirates-actor-shah-rukh-khan-waves-to-fans-ahead-of-an-q-a-session-during.jpg?s=612x612&w=0&k=20&c=a0c7cL7UnuZGl9aoByjWKle7VE5p-cBDGXHnqWn2_eQ=",
      alt: "Shah Rukh Khan - Actor",
      title: "The Actor",
      year: "Legend"
    },
    {
      id: 6,
      src: " https://media.gettyimages.com/id/1172864793/photo/port-of-spain-trinidad-and-tobago-in-this-handout-image-provided-by-cpl-t20-shah-rukh-khan.jpg?s=612x612&w=0&k=20&c=fG0zfdS7dz36HzKr4BDDYvwkNptKSP7HOhDKW79pQlI=",
      alt: "SRK - Performer",
      title: "The Performer",
      year: "Stage"
    }
  ];

  const movies = [
    { title: "Dilwale Dulhania Le Jayenge", year: "1995", rating: "9.1", color: "from-pink-100 to-rose-100" },
    { title: "Kabhi Khushi Kabhie Gham", year: "2001", rating: "7.5", color: "from-rose-100 to-fuchsia-100" },
    { title: "Chennai Express", year: "2013", rating: "6.0", color: "from-rose-50 to-fuchsia-50" }
  ];

  const awards = [
    { name: "Padma Shri", year: "2005", category: "National Award", icon: "🏅", color: "bg-gradient-to-r from-pink-200 to-rose-200" },
    { name: "Best Actor", year: "2008", category: "Filmfare", icon: "🎭", color: "bg-gradient-to-r from-rose-200 to-pink-200" },
    { name: "CNN-IBN Indian of the Year", year: "2013", category: "Entertainment", icon: "🏆", color: "bg-gradient-to-r from-pink-100 to-purple-100" },
    { name: "Honorary Doctorate", year: "2015", category: "University", icon: "🎓", color: "bg-gradient-to-r from-rose-100 to-fuchsia-100" }
  ];

  const stats = [
    { icon: <Film className="w-6 h-6" />, label: "Movies", value: "100+", color: "text-pink-600", bg: "bg-pink-100" },
    { icon: <Award className="w-6 h-6" />, label: "Awards", value: "300+", color: "text-rose-600", bg: "bg-rose-100" },
    { icon: <Globe className="w-6 h-6" />, label: "Global Fans", value: "3.5B+", color: "text-fuchsia-600", bg: "bg-fuchsia-100" },
    { icon: <TrendingUp className="w-6 h-6" />, label: "Box Office", value: "$1B+", color: "text-purple-600", bg: "bg-purple-100" }
  ];

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 30 - 15,
        y: (e.clientY / window.innerHeight) * 30 - 15
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll progress effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const heroHeight = containerRef.current?.clientHeight || 800;
      const progress = (scrollTop / heroHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated gradient colors
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientAngle((prev) => (prev + 0.3) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Animation phase for wave effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase((prev) => (prev + 0.5) % 360);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Generate floating elements data
  const generateFloatingElements = (count, isMobile) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * (isMobile ? 15 : 25) + 8,
      delay: Math.random() * 5,
      duration: Math.random() * 20 + 15,
      type: i % 6, // 0-5 for different element types
      rotation: Math.random() * 360
    }));
  };

  const [floatingElements, setFloatingElements] = useState([]);
  
  useEffect(() => {
    setFloatingElements(generateFloatingElements(35, window.innerWidth < 768));
  }, []);

  const toggleLike = () => setLiked(!liked);
  const openLightbox = () => setPlayTrailer(true);
  const closeLightbox = () => setPlayTrailer(false);

  const navigateImage = (direction) => {
    if (direction === 'next') {
      setSelectedImage((prev) => (prev + 1) % images.length);
    } else {
      setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  return (
    <>
      <section 
        ref={containerRef}
        id="actor" 
        className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100 py-16 px-4 sm:px-6 lg:px-8"
        style={{
          '--gradient-angle': `${gradientAngle}deg`,
          '--animation-phase': `${animationPhase}deg`,
          background: `radial-gradient(circle at ${mousePosition.x + 50}% ${mousePosition.y + 50}%, rgba(255, 182, 193, 0.3) 0%, transparent 60%),
                      radial-gradient(circle at ${100 - mousePosition.x + 50}% ${100 - mousePosition.y + 50}%, rgba(251, 207, 232, 0.35) 0%, transparent 60%),
                      linear-gradient(var(--gradient-angle), rgba(255, 240, 245, 0.95) 0%, rgba(255, 228, 230, 0.85) 50%, rgba(255, 240, 245, 0.95) 100%)`
        }}
      >
        {/* Dynamic background grid */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255, 182, 193, 0.15) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255, 182, 193, 0.15) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            maskImage: 'radial-gradient(circle at center, black 30%, transparent 70%)'
          }} />
        </div>

        {/* Animated wave background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-rose-200/10 via-transparent to-transparent animate-wave"></div>
        </div>

        {/* Floating Bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 0).map((bubble) => (
            <div
              key={`bubble-${bubble.id}`}
              className="absolute rounded-full animate-bubble-float"
              style={{
                left: `${bubble.x}%`,
                top: `${bubble.y}%`,
                width: `${bubble.size}px`,
                height: `${bubble.size}px`,
                background: `radial-gradient(circle at 30% 30%, 
                  rgba(255, 255, 255, 0.9) 0%, 
                  rgba(255, 255, 255, 0.7) 30%,
                  rgba(255, 200, 221, 0.4) 70%,
                  transparent 100%
                )`,
                border: '1px solid rgba(255, 255, 255, 0.5)',
                animationDelay: `${bubble.delay}s`,
                animationDuration: `${bubble.duration}s`,
                filter: 'blur(0.5px)',
                boxShadow: 'inset 0 0 15px rgba(255, 255, 255, 0.6), 0 0 25px rgba(255, 182, 193, 0.4)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 1).map((star) => (
            <div
              key={`star-${star.id}`}
              className="absolute animate-star-twinkle"
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: `${star.size / 2}px`,
                height: `${star.size / 2}px`,
                background: `radial-gradient(circle, 
                  rgba(255, 255, 255, 0.95) 0%,
                  rgba(255, 255, 255, 0.8) 30%,
                  rgba(255, 182, 193, 0.6) 70%,
                  transparent 100%
                )`,
                clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`,
                filter: 'drop-shadow(0 0 3px rgba(255, 182, 193, 0.6))',
                willChange: 'transform, opacity, filter'
              }}
            />
          ))}
        </div>

        {/* Sparkle Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 2).map((sparkle) => (
            <div
              key={`sparkle-${sparkle.id}`}
              className="absolute animate-sparkle-float"
              style={{
                left: `${sparkle.x}%`,
                top: `${sparkle.y}%`,
                width: `${sparkle.size / 3}px`,
                height: `${sparkle.size / 3}px`,
                background: `radial-gradient(circle, 
                  rgba(255, 255, 255, 1) 0%,
                  rgba(255, 255, 255, 0.9) 50%,
                  rgba(244, 114, 182, 0.5) 100%
                )`,
                animationDelay: `${sparkle.delay}s`,
                animationDuration: `${Math.random() * 3 + 2}s`,
                transform: `rotate(${sparkle.rotation}deg)`,
                filter: 'blur(0.5px)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Hearts */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 3).map((heart) => (
            <div
              key={`heart-${heart.id}`}
              className="absolute animate-heart-float"
              style={{
                left: `${heart.x}%`,
                top: `${heart.y}%`,
                width: `${heart.size / 2}px`,
                height: `${heart.size / 2}px`,
                background: `linear-gradient(135deg, 
                  rgba(255, 100, 150, 0.3) 0%,
                  rgba(255, 150, 200, 0.2) 50%,
                  rgba(255, 100, 150, 0.1) 100%
                )`,
                clipPath: 'polygon(50% 0%, 100% 35%, 82% 100%, 50% 75%, 18% 100%, 0% 35%)',
                animationDelay: `${heart.delay}s`,
                animationDuration: `${heart.duration}s`,
                filter: 'blur(1px)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Diamonds */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 4).map((diamond) => (
            <div
              key={`diamond-${diamond.id}`}
              className="absolute animate-diamond-float"
              style={{
                left: `${diamond.x}%`,
                top: `${diamond.y}%`,
                width: `${diamond.size / 2}px`,
                height: `${diamond.size / 2}px`,
                background: `linear-gradient(135deg, 
                  rgba(255, 255, 255, 0.9) 0%,
                  rgba(244, 114, 182, 0.5) 50%,
                  rgba(236, 72, 153, 0.3) 100%
                )`,
                clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                animationDelay: `${diamond.delay}s`,
                animationDuration: `${diamond.duration}s`,
                transform: `rotate(${diamond.rotation}deg)`,
                filter: 'drop-shadow(0 0 4px rgba(244, 114, 182, 0.4))',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Crowns */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 5).map((crown) => (
            <div
              key={`crown-${crown.id}`}
              className="absolute animate-crown-float"
              style={{
                left: `${crown.x}%`,
                top: `${crown.y}%`,
                fontSize: `${crown.size / 10}px`,
                animationDelay: `${crown.delay}s`,
                animationDuration: `${crown.duration}s`,
                color: 'rgba(236, 72, 153, 0.3)',
                filter: 'drop-shadow(0 0 2px rgba(236, 72, 153, 0.4))',
                willChange: 'transform, opacity'
              }}
            >
              ♛
            </div>
          ))}
        </div>

        {/* Animated gradient orbs */}
        <div 
          className="absolute top-20 left-10 w-48 h-48 bg-gradient-to-br from-pink-400/25 via-rose-400/25 to-pink-400/15 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-orb-float hidden md:block"
          style={{
            transform: `translate(${mousePosition.x * 0.4}px, ${mousePosition.y * 0.4}px) rotate(${mousePosition.x * 0.5}deg)`,
            animationDelay: '0.5s'
          }}
        />
        
        <div 
          className="absolute bottom-32 right-10 w-56 h-56 bg-gradient-to-tr from-rose-300/30 via-pink-300/30 to-rose-300/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-orb-float-slow hidden md:block"
          style={{
            transform: `translate(${-mousePosition.x * 0.3}px, ${-mousePosition.y * 0.3}px) rotate(${-mousePosition.x * 0.3}deg)`,
            animationDelay: '1s'
          }}
        />

        {/* Animated decorative icons with trails */}
        <div className="hidden md:block">
          <div className="absolute top-20 right-20 group cursor-pointer">
            <div className="absolute inset-0 bg-pink-400/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Sparkles 
              className="relative text-pink-400/60 w-10 h-10 animate-spin-slow group-hover:text-pink-500 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.1}px, ${mousePosition.y * 0.1}px)` }}
            />
          </div>
          
          <div className="absolute bottom-32 left-20 group cursor-pointer">
            <div className="absolute inset-0 bg-rose-400/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Heart 
              className="relative text-rose-400/70 w-8 h-8 animate-pulse group-hover:text-rose-500 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.2}px, ${mousePosition.y * 0.2}px)` }}
            />
          </div>
          
          <div className="absolute top-32 left-1/3 group cursor-pointer">
            <div className="absolute inset-0 bg-pink-500/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Star 
              className="relative text-pink-500/50 w-9 h-9 animate-spin group-hover:text-pink-600 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.15}px, ${mousePosition.y * 0.15}px)` }}
            />
          </div>
        </div>

        {/* Scroll progress indicator with glow effect */}
        <div className="fixed top-0 left-0 right-0 h-1 z-50">
          <div 
            className="h-full bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 transition-all duration-300 shadow-lg"
            style={{ width: `${scrollProgress}%` }}
          />
          <div 
            className="absolute top-0 h-full w-full bg-gradient-to-r from-pink-400/50 via-rose-400/50 to-pink-400/50 blur-lg transition-all duration-300"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header Section with enhanced animation */}
          <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 transform animate-slide-up-stagger`}>
            {/* Animated crowns */}
            <div className="inline-flex items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8 animate-bounce-slow relative">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-400/20 via-rose-400/20 to-pink-400/20 blur-2xl rounded-full opacity-50"></div>
              <Crown className="relative w-8 h-8 sm:w-10 sm:h-10 text-rose-500 animate-icon-float" style={{ animationDelay: '0.2s' }} />
              <Star className="relative w-10 h-10 sm:w-12 sm:h-12 text-pink-500 fill-pink-500 animate-icon-float-slow" style={{ animationDelay: '0.4s' }} />
              <Crown className="relative w-8 h-8 sm:w-10 sm:h-10 text-rose-500 animate-icon-float" style={{ animationDelay: '0.6s' }} />
            </div>
            
            {/* Main title with gradient animation */}
            <div className="relative mb-4 sm:mb-6">
              <div className="absolute -inset-4 sm:-inset-6 bg-gradient-to-r from-rose-600/20 via-pink-600/20 to-rose-600/20 blur-xl rounded-full opacity-50"></div>
              <h1 className="relative text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-600 via-pink-600 to-rose-600 animate-gradient-flow animate-slide-up-stagger">
                Shah Rukh Khan
              </h1>
              <div className="absolute -bottom-2 sm:-bottom-3 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent animate-width-pulse"></div>
            </div>
            
            {/* Tag buttons with floating effect */}
            <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <span className="group relative px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-lg font-semibold shadow-xl shadow-pink-300 hover:shadow-2xl hover:shadow-pink-400 transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 overflow-hidden animate-button-float">
                <div className="absolute inset-0 bg-gradient-to-r from-rose-500 to-pink-500 animate-gradient-x"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-rose-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <span className="relative flex items-center gap-1 sm:gap-2 text-white">
                  <Crown className="w-4 h-4 sm:w-5 sm:h-5" />
                  King of Bollywood
                </span>
              </span>
              
              <span className="group relative px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-lg font-semibold transition-all duration-500 transform hover:scale-105 overflow-hidden animate-button-float-slow" style={{ animationDelay: '0.3s' }}>
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm border border-pink-200 group-hover:bg-white group-hover:shadow-lg group-hover:border-pink-300"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-pink-100/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <span className="relative text-gray-700">Romance King</span>
              </span>
              
              <span className="group relative px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-lg font-semibold transition-all duration-500 transform hover:scale-105 overflow-hidden animate-button-float-slower" style={{ animationDelay: '0.6s' }}>
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm border border-pink-200 group-hover:bg-white group-hover:shadow-lg group-hover:border-pink-300"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-rose-100/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <span className="relative text-gray-700">Global Icon</span>
              </span>
            </div>
            
            {/* Description with animated border */}
            <div className="relative mb-8 sm:mb-10">
              <div className="absolute -inset-4 sm:-inset-6 bg-gradient-to-r from-pink-400/10 via-rose-400/10 to-pink-400/10 blur-lg rounded-2xl opacity-50 animate-pulse-glow"></div>
              <p className="relative text-base sm:text-xl text-gray-700 max-w-3xl sm:max-w-4xl mx-auto leading-relaxed font-medium bg-white/50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl border border-pink-100 shadow-lg animate-fade-in-stagger">
                Known as the <span className="text-rose-600 font-bold">"Baadshah of Bollywood"</span>, Shah Rukh Khan is one of the most successful and influential actors in the world. 
                With a career spanning over three decades, he has captured hearts globally with his charm, talent, and dedication.
              </p>
            </div>

            {/* Stats Grid with enhanced animations */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-2xl sm:max-w-3xl mx-auto mb-8 sm:mb-12">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="group relative bg-white/90 backdrop-blur-md rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-pink-100 shadow-lg hover:shadow-xl hover:border-pink-200 transition-all duration-500 transform hover:-translate-y-3 animate-stat-float overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Background glow on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-20 transition-opacity duration-500" 
                       style={{ background: `linear-gradient(to bottom right, ${stat.color.replace('text-', '')}, ${stat.color.replace('text-', '')})` }}></div>
                  
                  <div className={`relative p-2 sm:p-3 ${stat.bg} rounded-lg sm:rounded-xl w-fit mb-3 sm:mb-4 overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <div className={stat.color}>{stat.icon}</div>
                  </div>
                  <div className={`relative text-2xl sm:text-3xl font-bold ${stat.color} mb-1 group-hover:scale-105 transition-transform duration-500`}>{stat.value}</div>
                  <div className="relative text-gray-600 text-sm sm:text-base">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16 animate-fade-in-stagger" style={{ animationDelay: '300ms' }}>
            {/* Featured Image Gallery with enhanced animations */}
            <div className="space-y-4 sm:space-y-6">
              <div className="relative group animate-card-float">
                {/* Glow effect */}
                <div className="absolute -inset-4 sm:-inset-6 bg-gradient-to-r from-rose-500/20 via-pink-500/20 to-rose-500/20 blur-xl rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-700"></div>
                
                <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl shadow-pink-200/50 bg-gradient-to-br from-pink-100 to-rose-100 p-1 sm:p-2">
                  <img 
                    src={images[selectedImage].src}
                    alt={images[selectedImage].alt}
                    className="w-full h-[350px] sm:h-[450px] md:h-[550px] object-cover transition-transform duration-1000 group-hover:scale-110 group-hover:rotate-1 rounded-xl sm:rounded-2xl"
                  />
                  
                  {/* Gradient Overlay with sparkles */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent">
                    {/* Sparkle effect */}
                    <div className="absolute inset-0">
                      {[...Array(3)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-2 h-2 bg-white rounded-full animate-sparkle"
                          style={{
                            left: `${Math.random() * 80 + 10}%`,
                            top: `${Math.random() * 60 + 20}%`,
                            animationDelay: `${i * 0.3}s`
                          }}
                        />
                      ))}
                    </div>
                    
                    <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-4 sm:left-6 md:left-8 right-4 sm:right-6 md:right-8">
                      <div className="transform transition-transform duration-700 group-hover:translate-y-0 translate-y-6">
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2 drop-shadow-lg group-hover:scale-105 transition-transform duration-500">{images[selectedImage].title}</h3>
                        <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm w-fit px-3 sm:px-4 py-1 sm:py-2 rounded-full group-hover:bg-white/30 transition-all duration-500">
                          <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-pulse-slow" />
                          <p className="text-white font-medium text-sm sm:text-base">{images[selectedImage].year}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Like Button with enhanced animation */}
                  <button 
                    onClick={toggleLike}
                    className="absolute top-4 sm:top-6 right-4 sm:right-6 p-2 sm:p-4 bg-white/90 backdrop-blur-md rounded-full hover:bg-white transition-all duration-500 transform hover:scale-110 active:scale-95 shadow-lg group/like relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 to-rose-400/0 group-hover/like:from-pink-400/20 group-hover/like:to-rose-400/20 transition-all duration-500"></div>
                    <Heart 
                      className={`relative w-5 h-5 sm:w-7 sm:h-7 transition-all duration-500 ${liked ? 'fill-rose-500 text-rose-500 animate-heart-pop' : 'text-gray-700'}`}
                    />
                  </button>
                </div>
                
                {/* Image Navigation with enhanced animations */}
                <div className="flex justify-center items-center gap-3 sm:gap-4 mt-4 sm:mt-6">
                  <button 
                    onClick={() => navigateImage('prev')}
                    className="group relative p-3 sm:p-4 bg-white/90 backdrop-blur-md rounded-full border border-pink-200 transition-all duration-500 transform hover:scale-110 hover:-translate-x-1 shadow-lg overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 to-rose-400/0 group-hover:from-pink-400/20 group-hover:to-rose-400/20 transition-all duration-500"></div>
                    <ChevronLeft className="relative w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-rose-600 transition-colors duration-500" />
                  </button>
                  
                  <div className="group relative bg-white/90 backdrop-blur-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 via-rose-400/0 to-pink-400/0 group-hover:from-pink-400/10 group-hover:via-rose-400/10 group-hover:to-pink-400/10 transition-all duration-500"></div>
                    <span className="relative font-semibold text-gray-700 text-sm sm:text-base group-hover:text-rose-700 transition-colors duration-500">
                      {selectedImage + 1} / {images.length}
                    </span>
                  </div>
                  
                  <button 
                    onClick={() => navigateImage('next')}
                    className="group relative p-3 sm:p-4 bg-white/90 backdrop-blur-md rounded-full border border-pink-200 transition-all duration-500 transform hover:scale-110 hover:translate-x-1 shadow-lg overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 to-rose-400/0 group-hover:from-pink-400/20 group-hover:to-rose-400/20 transition-all duration-500"></div>
                    <ChevronRight className="relative w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-pink-600 transition-colors duration-500" />
                  </button>
                </div>
              </div>

              {/* Thumbnail Gallery with hover effects */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
                {images.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => setSelectedImage(index)}
                    className={`group relative overflow-hidden rounded-lg sm:rounded-xl transition-all duration-500 transform hover:scale-110 shadow-lg ${
                      selectedImage === index 
                        ? 'ring-2 sm:ring-3 ring-rose-500 ring-offset-1 sm:ring-offset-2 ring-offset-pink-50 scale-105 shadow-xl shadow-rose-300/50' 
                        : 'hover:ring-2 hover:ring-pink-300'
                    }`}
                  >
                    {/* Glow effect for active thumbnail */}
                    {selectedImage === index && (
                      <div className="absolute -inset-1 bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg blur-sm opacity-50"></div>
                    )}
                    
                    <img 
                      src={image.src}
                      alt={image.alt}
                      className="relative w-full h-16 sm:h-20 md:h-24 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-1 sm:p-2 transition-all duration-500 ${
                      selectedImage === index ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}>
                      <span className="text-white text-xs sm:text-sm font-medium truncate group-hover:scale-105 transition-transform duration-500">{image.title}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Actor Info Panel with enhanced animations */}
            <div className="space-y-6 sm:space-y-8">
              {/* Personal Info Card */}
              <div className="group relative bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-pink-100 shadow-xl sm:shadow-2xl shadow-pink-200/30 animate-card-float-slow overflow-hidden">
                {/* Background glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-rose-500/0 via-pink-500/0 to-rose-500/0 group-hover:from-rose-500/5 group-hover:via-pink-500/5 group-hover:to-rose-500/5 transition-all duration-700"></div>
                
                <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8 relative">
                  <div className="group/icon relative p-3 sm:p-4 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover/icon:translate-x-full transition-transform duration-700"></div>
                    <Award className="w-6 h-6 sm:w-8 sm:h-8 text-white relative" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 group-hover:text-rose-700 transition-colors duration-500">Film Legend</h3>
                    <p className="text-gray-600 text-sm sm:text-base">The King of Romance</p>
                  </div>
                </div>
                
                <div className="space-y-3 sm:space-y-4 relative">
                  <div className="group/item relative flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl hover:from-pink-100 hover:to-rose-100 transition-all duration-500 border border-pink-100 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover/item:translate-x-full transition-transform duration-700"></div>
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-rose-500 relative group-hover/item:scale-110 transition-transform duration-500" />
                    <div className="relative">
                      <h4 className="font-semibold text-gray-700 mb-1 text-sm sm:text-base">Born</h4>
                      <p className="text-gray-800 font-medium text-sm sm:text-base">November 2, 1965 • New Delhi, India</p>
                    </div>
                  </div>
                  
                  <div className="group/item relative flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl hover:from-pink-100 hover:to-rose-100 transition-all duration-500 border border-pink-100 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover/item:translate-x-full transition-transform duration-700"></div>
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-rose-500 relative group-hover/item:scale-110 transition-transform duration-500" />
                    <div className="relative">
                      <h4 className="font-semibold text-gray-700 mb-1 text-sm sm:text-base">Family</h4>
                      <p className="text-gray-800 font-medium text-sm sm:text-base">Gauri Khan (Spouse) • 3 Children</p>
                    </div>
                  </div>
                  
                  <div className="group/item relative p-3 sm:p-4 bg-gradient-to-r from-rose-500 to-pink-500 rounded-xl sm:rounded-xl shadow-lg overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover/item:translate-x-full transition-transform duration-700"></div>
                    <h4 className="relative font-semibold text-white mb-1 text-sm sm:text-base">Net Worth</h4>
                    <p className="relative text-2xl sm:text-3xl font-bold text-white group-hover/item:scale-105 transition-transform duration-500">$750 Million</p>
                    <p className="relative text-white/80 text-xs sm:text-sm mt-1">World's Richest Actor (India)</p>
                  </div>
                </div>
                
                {/* Watch button with enhanced animation */}
                {/* <button 
                  onClick={openLightbox}
                  className="group relative w-full mt-6 sm:mt-8 py-3 sm:py-4 overflow-hidden rounded-xl font-semibold transition-all duration-500 transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 sm:gap-3 shadow-xl shadow-pink-300/50 hover:shadow-2xl hover:shadow-pink-400/50"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-rose-500 to-pink-500 animate-gradient-x"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-rose-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white relative transition-transform group-hover:scale-110" />
                  <span className="text-sm sm:text-base text-white relative">Watch Iconic Moments</span>
                </button> */}
                
                {/* Social Links with enhanced animations */}
                {/* <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8 justify-center">
                  <a href="#" className="group relative p-3 sm:p-4 rounded-full transition-all duration-500 transform hover:scale-110 shadow-lg border border-pink-200 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-100 to-rose-100 group-hover:from-pink-200 group-hover:to-rose-200"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <Instagram className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600 group-hover:text-pink-700 relative" />
                  </a>
                  <a href="#" className="group relative p-3 sm:p-4 rounded-full transition-all duration-500 transform hover:scale-110 shadow-lg border border-pink-200 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-100 to-rose-100 group-hover:from-pink-200 group-hover:to-rose-200"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <Twitter className="w-5 h-5 sm:w-6 sm:h-6 text-sky-500 group-hover:text-sky-600 relative" />
                  </a>
                  <a href="#" className="group relative p-3 sm:p-4 rounded-full transition-all duration-500 transform hover:scale-110 shadow-lg border border-pink-200 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-100 to-rose-100 group-hover:from-pink-200 group-hover:to-rose-200"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <Youtube className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 group-hover:text-red-600 relative" />
                  </a>
                </div> */}
              </div>

              {/* Quote Card with enhanced animations */}
              <div className="group relative bg-gradient-to-br from-rose-100 via-pink-100 to-purple-100 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-pink-200 shadow-xl animate-quote-float overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-rose-300"></div>
                  <div className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-pink-300"></div>
                </div>
                
                <div className="relative">
                  <Star className="absolute -top-2 sm:-top-3 -left-2 sm:-left-3 w-5 h-5 sm:w-6 sm:h-6 text-rose-400 animate-spin-slow" />
                  <Star className="absolute -bottom-2 sm:-bottom-3 -right-2 sm:-right-3 w-5 h-5 sm:w-6 sm:h-6 text-pink-400 animate-spin-slow" />
                  <blockquote className="text-lg sm:text-xl md:text-2xl italic text-gray-800 text-center leading-relaxed font-medium group-hover:scale-105 transition-transform duration-500">
                    "Success is not a good teacher, failure makes you humble."
                  </blockquote>
                </div>
                <p className="text-rose-600 text-center mt-3 sm:mt-4 font-bold text-sm sm:text-base group-hover:scale-105 transition-transform duration-500">— Shah Rukh Khan</p>
              </div>
            </div>
          </div>

          {/* Movies & Awards Section with enhanced animations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16 animate-fade-in-stagger" style={{ animationDelay: '600ms' }}>
            {/* Iconic Movies */}
            <div className="group relative bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-pink-100 shadow-xl sm:shadow-2xl shadow-pink-200/30 overflow-hidden animate-card-float">
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 via-rose-500/0 to-pink-500/0 group-hover:from-pink-500/5 group-hover:via-rose-500/5 group-hover:to-pink-500/5 transition-all duration-700"></div>
              
              <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8 relative">
                <div className="group/icon relative p-2 sm:p-3 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg sm:rounded-xl shadow-lg overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover/icon:translate-x-full transition-transform duration-700"></div>
                  <Film className="w-5 h-5 sm:w-7 sm:h-7 text-white relative" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-800 group-hover:text-pink-700 transition-colors duration-500">Top Three Movies</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Top-rated performances</p>
                </div>
              </div>
              <div className="space-y-3 sm:space-y-4 relative">
                {movies.map((movie, index) => (
                  <div
                    key={index}
                    onMouseEnter={() => setHoveredMovie(index)}
                    onMouseLeave={() => setHoveredMovie(null)}
                    className={`group/item relative overflow-hidden rounded-xl p-4 sm:p-5 transition-all duration-500 transform ${hoveredMovie === index ? 'scale-[1.02] shadow-lg' : ''} border border-pink-100 bg-gradient-to-r ${movie.color} overflow-hidden`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full group-hover/item:translate-x-full transition-transform duration-700"></div>
                    <div className="relative flex items-center justify-between">
                      <div className="flex-1 pr-2">
                        <h4 className="font-bold text-gray-800 text-base sm:text-lg mb-1 group-hover/item:scale-105 transition-transform duration-500">{movie.title}</h4>
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 animate-pulse-slow" />
                          <p className="text-gray-600 text-xs sm:text-sm">{movie.year}</p>
                        </div>
                      </div>
                      <div className="group/rating relative flex items-center gap-1 sm:gap-2 bg-white/80 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 to-yellow-500/0 group-hover/rating:from-amber-500/10 group-hover/rating:to-yellow-500/10 transition-all duration-500"></div>
                        <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 fill-amber-500 relative" />
                        <span className="font-bold text-gray-800 text-sm sm:text-base relative">{movie.rating}</span>
                        <span className="text-gray-600 text-xs sm:text-sm relative">/10</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Awards & Honors */}
            <div className="group relative bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-pink-100 shadow-xl sm:shadow-2xl shadow-pink-200/30 overflow-hidden animate-card-float-slow">
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-rose-500/0 via-fuchsia-500/0 to-rose-500/0 group-hover:from-rose-500/5 group-hover:via-fuchsia-500/5 group-hover:to-rose-500/5 transition-all duration-700"></div>
              
              <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8 relative">
                <div className="group/icon relative p-2 sm:p-3 bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-lg sm:rounded-xl shadow-lg overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover/icon:translate-x-full transition-transform duration-700"></div>
                  <Award className="w-5 h-5 sm:w-7 sm:h-7 text-white relative" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-800 group-hover:text-rose-700 transition-colors duration-500">Major Awards</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Recognitions & Honors</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {awards.map((award, index) => (
                  <div
                    key={index}
                    className="group/item relative bg-white border border-pink-100 rounded-xl p-4 sm:p-5 transition-all duration-500 transform hover:-translate-y-2 hover:shadow-lg overflow-hidden animate-award-float"
                    style={{ animationDelay: `${index * 100 + 700}ms` }}
                  >
                    {/* Background glow on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover/item:opacity-20 transition-opacity duration-500" 
                         style={{ background: `linear-gradient(to bottom right, ${award.color.replace('bg-gradient-to-r ', '').split(' ')[0]}, ${award.color.replace('bg-gradient-to-r ', '').split(' ')[2]})` }}></div>
                    
                    <div className="flex items-start justify-between mb-2 sm:mb-3 relative">
                      <span className="text-2xl sm:text-3xl relative group-hover/item:scale-110 transition-transform duration-500">{award.icon}</span>
                      <span className="group/year relative px-2 sm:px-3 py-1 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-semibold rounded-full shadow overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-rose-600 to-pink-600 opacity-0 group-hover/year:opacity-100 transition-opacity duration-500"></div>
                        <span className="relative">{award.year}</span>
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-800 text-sm sm:text-base mb-1 relative group-hover/item:scale-105 transition-transform duration-500">{award.name}</h4>
                    <p className="text-gray-600 text-xs sm:text-sm relative">{award.category}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trailer Modal with enhanced animations */}
      {playTrailer && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 animate-modal-fade-in"
          onClick={closeLightbox}
        >
          {/* Animated backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/90 to-black/95 backdrop-blur-xl transition-all duration-700"></div>
          
          {/* Floating particles in lightbox */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-lightbox-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${Math.random() * 3 + 1}px`,
                  height: `${Math.random() * 3 + 1}px`,
                  background: `radial-gradient(circle, rgba(244, 114, 182, 0.6) 0%, rgba(244, 114, 182, 0) 70%)`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${Math.random() * 10 + 5}s`,
                  filter: 'blur(0.5px)'
                }}
              />
            ))}
          </div>

          {/* Modal Content */}
          <div 
            className="relative max-w-2xl sm:max-w-3xl md:max-w-4xl w-full animate-modal-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button with glow */}
            <button 
              onClick={closeLightbox}
              className="group absolute -top-8 sm:-top-12 right-0 p-2 sm:p-3 bg-white/90 hover:bg-white rounded-full transition-all duration-500 transform hover:scale-110 shadow-lg z-10 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 relative" />
            </button>
            
            <div className="relative bg-gradient-to-br from-white via-pink-50 to-rose-50 rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden shadow-3xl shadow-pink-300/50 border border-pink-200">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-400 via-rose-400 to-pink-400 animate-gradient-x"></div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-400 via-rose-400 to-pink-400 animate-gradient-x"></div>
              </div>
              
              {/* Video Placeholder with enhanced animation */}
              <div className="relative aspect-video bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-rose-300/20 via-transparent to-pink-300/20 animate-gradient-x" />
                <div className="absolute inset-0">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-4 h-4 bg-white rounded-full animate-sparkle"
                      style={{
                        left: `${Math.random() * 90 + 5}%`,
                        top: `${Math.random() * 80 + 10}%`,
                        animationDelay: `${i * 0.2}s`
                      }}
                    />
                  ))}
                </div>
                <button className="group relative z-10 p-6 sm:p-8 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full hover:from-rose-600 hover:to-pink-600 transition-all duration-500 transform hover:scale-110 shadow-2xl shadow-rose-400 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <Play className="w-12 h-12 sm:w-16 sm:h-16 text-white relative group-hover:scale-110 transition-transform duration-500" />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="p-4 sm:p-6 md:p-8 relative">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:scale-105 transition-transform duration-500">Iconic Moments Compilation</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">A collection of Shah Rukh Khan's most memorable scenes from his illustrious career</p>
                
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <span className="group relative px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border border-rose-200 overflow-hidden">
                    <div className="absolute inset-0 bg-rose-100"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative text-rose-700">3 Hours</span>
                  </span>
                  <span className="group relative px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border border-pink-200 overflow-hidden">
                    <div className="absolute inset-0 bg-pink-100"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative text-pink-700">HD Quality</span>
                  </span>
                  <span className="group relative px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border border-fuchsia-200 overflow-hidden">
                    <div className="absolute inset-0 bg-fuchsia-100"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <span className="relative text-fuchsia-700">Best Scenes</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Instructions */}
          <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 text-xs sm:text-sm text-center px-4 animate-fade-in" style={{ animationDelay: '400ms' }}>
            <div className="flex items-center gap-2 animate-pulse-slow">
              <span>Tap outside to close</span>
              <span className="text-pink-300">•</span>
              <span>Enjoy the compilation</span>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes bubble-float {
          0%, 100% {
            transform: translateY(0) translateX(0) scale(1) rotate(0deg);
            opacity: 0.3;
          }
          25% {
            transform: translateY(-40px) translateX(15px) scale(1.15) rotate(5deg);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-70px) translateX(-10px) scale(1) rotate(0deg);
            opacity: 0.4;
          }
          75% {
            transform: translateY(-25px) translateX(20px) scale(1.1) rotate(-5deg);
            opacity: 0.6;
          }
        }

        @keyframes star-twinkle {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1) rotate(0deg);
            filter: drop-shadow(0 0 2px rgba(255, 182, 193, 0.6));
          }
          25% {
            opacity: 0.9;
            transform: scale(1.3) rotate(90deg);
            filter: drop-shadow(0 0 5px rgba(255, 182, 193, 0.9));
          }
          50% {
            opacity: 0.4;
            transform: scale(0.95) rotate(180deg);
            filter: drop-shadow(0 0 3px rgba(255, 182, 193, 0.7));
          }
          75% {
            opacity: 0.8;
            transform: scale(1.2) rotate(270deg);
            filter: drop-shadow(0 0 4px rgba(255, 182, 193, 0.8));
          }
        }

        @keyframes sparkle-float {
          0%, 100% {
            transform: translateY(0) rotate(0deg) scale(1);
            opacity: 0.2;
          }
          50% {
            transform: translateY(-30px) rotate(180deg) scale(1.8);
            opacity: 0.9;
          }
        }

        @keyframes heart-float {
          0%, 100% {
            transform: translateY(0) scale(1) rotate(0deg);
            opacity: 0.2;
          }
          25% {
            transform: translateY(-50px) scale(1.2) rotate(45deg);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-90px) scale(0.9) rotate(90deg);
            opacity: 0.3;
          }
          75% {
            transform: translateY(-35px) scale(1.1) rotate(135deg);
            opacity: 0.5;
          }
        }

        @keyframes diamond-float {
          0%, 100% {
            transform: translateY(0) rotate(45deg) scale(1);
            opacity: 0.3;
          }
          33% {
            transform: translateY(-60px) rotate(135deg) scale(1.3);
            opacity: 0.8;
          }
          66% {
            transform: translateY(-100px) rotate(225deg) scale(0.95);
            opacity: 0.4;
          }
        }

        @keyframes crown-float {
          0%, 100% {
            transform: translateY(0) translateX(0) scale(1);
            opacity: 0.2;
          }
          25% {
            transform: translateY(-45px) translateX(20px) scale(1.2);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-80px) translateX(-15px) scale(0.9);
            opacity: 0.3;
          }
          75% {
            transform: translateY(-30px) translateX(25px) scale(1.1);
            opacity: 0.6;
          }
        }

        @keyframes orb-float {
          0%, 100% {
            transform: translateY(0) rotate(0deg) scale(1);
          }
          50% {
            transform: translateY(-25px) rotate(-3deg) scale(1.05);
          }
        }

        @keyframes orb-float-slow {
          0%, 100% {
            transform: translateY(0) rotate(0deg) scale(1);
          }
          33% {
            transform: translateY(-15px) rotate(2deg) scale(1.03);
          }
          66% {
            transform: translateY(-10px) rotate(-1deg) scale(0.98);
          }
        }

        @keyframes wave {
          0%, 100% {
            transform: translateX(0) scaleY(1);
          }
          25% {
            transform: translateX(-25%) scaleY(1.15);
          }
          50% {
            transform: translateX(-50%) scaleY(0.9);
          }
          75% {
            transform: translateX(-75%) scaleY(1.1);
          }
        }

        @keyframes gradient-x {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes gradient-flow {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes slide-up-stagger {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes fade-in-stagger {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes width-pulse {
          0%, 100% {
            width: 0;
            left: 50%;
          }
          50% {
            width: 100%;
            left: 0;
          }
        }

        @keyframes pulse-glow {
          0%, 100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.4;
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-15px);
          }
        }

        @keyframes button-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        @keyframes button-float-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }

        @keyframes button-float-slower {
          0%, 100% {
            transform: translateY(0);
          }
          25%, 75% {
            transform: translateY(-3px);
          }
        }

        @keyframes card-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes card-float-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-7px);
          }
        }

        @keyframes quote-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }

        @keyframes stat-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }

        @keyframes award-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }

        @keyframes heart-pop {
          0%, 100% {
            transform: scale(1);
          }
          25% {
            transform: scale(1.3);
          }
          50% {
            transform: scale(0.9);
          }
          75% {
            transform: scale(1.1);
          }
        }

        @keyframes icon-float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          33% {
            transform: translateY(-10px) rotate(5deg);
          }
          66% {
            transform: translateY(-5px) rotate(-3deg);
          }
        }

        @keyframes icon-float-slow {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-15px) scale(1.1);
          }
        }

        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        @keyframes modal-fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes modal-slide-up {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes lightbox-particle {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.2;
          }
          50% {
            transform: translateY(-50px) translateX(20px);
            opacity: 0.8;
          }
        }

        /* Animation classes */
        .animate-bubble-float {
          animation: bubble-float 25s ease-in-out infinite;
          will-change: transform, opacity;
        }

        .animate-star-twinkle {
          animation: star-twinkle 18s ease-in-out infinite;
          will-change: transform, opacity, filter;
        }

        .animate-sparkle-float {
          animation: sparkle-float 5s ease-in-out infinite;
          will-change: transform, opacity;
        }

        .animate-heart-float {
          animation: heart-float 20s ease-in-out infinite;
          will-change: transform, opacity;
        }

        .animate-diamond-float {
          animation: diamond-float 22s ease-in-out infinite;
          will-change: transform, opacity;
        }

        .animate-crown-float {
          animation: crown-float 15s ease-in-out infinite;
          will-change: transform, opacity;
        }

        .animate-orb-float {
          animation: orb-float 15s ease-in-out infinite;
          will-change: transform;
        }

        .animate-orb-float-slow {
          animation: orb-float-slow 20s ease-in-out infinite;
          will-change: transform;
        }

        .animate-wave {
          animation: wave 30s linear infinite;
          will-change: transform;
        }

        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
          background-size: 200% 200%;
          will-change: background-position;
        }

        .animate-gradient-flow {
          animation: gradient-flow 3s ease infinite;
          background-size: 200% auto;
          will-change: background-position;
        }

        .animate-slide-up-stagger {
          animation: slide-up-stagger 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-fade-in-stagger {
          animation: fade-in-stagger 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-width-pulse {
          animation: width-pulse 3s ease-in-out infinite;
          will-change: width, left;
        }

        .animate-pulse-glow {
          animation: pulse-glow 4s ease-in-out infinite;
          will-change: opacity;
        }

        .animate-spin {
          animation: spin 20s linear infinite;
          will-change: transform;
        }

        .animate-spin-slow {
          animation: spin-slow 30s linear infinite;
          will-change: transform;
        }

        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
          will-change: transform;
        }

        .animate-button-float {
          animation: button-float 6s ease-in-out infinite;
          will-change: transform;
        }

        .animate-button-float-slow {
          animation: button-float-slow 7s ease-in-out infinite;
          will-change: transform;
        }

        .animate-button-float-slower {
          animation: button-float-slower 8s ease-in-out infinite;
          will-change: transform;
        }

        .animate-card-float {
          animation: card-float 8s ease-in-out infinite;
          will-change: transform;
        }

        .animate-card-float-slow {
          animation: card-float-slow 9s ease-in-out infinite;
          will-change: transform;
        }

        .animate-quote-float {
          animation: quote-float 10s ease-in-out infinite;
          will-change: transform;
        }

        .animate-stat-float {
          animation: stat-float 7s ease-in-out infinite;
          will-change: transform;
        }

        .animate-award-float {
          animation: award-float 6s ease-in-out infinite;
          will-change: transform;
        }

        .animate-heart-pop {
          animation: heart-pop 0.5s ease-in-out;
          will-change: transform;
        }

        .animate-icon-float {
          animation: icon-float 6s ease-in-out infinite;
          will-change: transform;
        }

        .animate-icon-float-slow {
          animation: icon-float-slow 8s ease-in-out infinite;
          will-change: transform;
        }

        .animate-sparkle {
          animation: sparkle 1.5s ease-in-out infinite;
          will-change: transform, opacity;
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
          will-change: transform, opacity;
        }

        .animate-modal-fade-in {
          animation: modal-fade-in 0.5s ease-out forwards;
        }

        .animate-modal-slide-up {
          animation: modal-slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-lightbox-particle {
          animation: lightbox-particle 15s ease-in-out infinite;
          will-change: transform, opacity;
        }

        /* Reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          .animate-bubble-float,
          .animate-star-twinkle,
          .animate-sparkle-float,
          .animate-heart-float,
          .animate-diamond-float,
          .animate-crown-float,
          .animate-orb-float,
          .animate-orb-float-slow,
          .animate-wave,
          .animate-gradient-x,
          .animate-gradient-flow,
          .animate-slide-up-stagger,
          .animate-fade-in-stagger,
          .animate-width-pulse,
          .animate-pulse-glow,
          .animate-spin,
          .animate-spin-slow,
          .animate-bounce-slow,
          .animate-button-float,
          .animate-button-float-slow,
          .animate-button-float-slower,
          .animate-card-float,
          .animate-card-float-slow,
          .animate-quote-float,
          .animate-stat-float,
          .animate-award-float,
          .animate-heart-pop,
          .animate-icon-float,
          .animate-icon-float-slow,
          .animate-sparkle,
          .animate-pulse-slow,
          .animate-modal-fade-in,
          .animate-modal-slide-up,
          .animate-lightbox-particle {
            animation: none !important;
          }
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .animate-bubble-float,
          .animate-star-twinkle,
          .animate-heart-float,
          .animate-diamond-float,
          .animate-crown-float {
            animation-duration: 15s !important;
          }
          
          .animate-wave {
            animation: none;
          }
          
          .animate-button-float,
          .animate-card-float,
          .animate-stat-float {
            animation-duration: 4s !important;
          }
        }
      `}</style>
    </>
  );
};

export default Actor;